import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Card,
  CardContent,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  LinearProgress,
  CircularProgress,
  Divider,
} from '@mui/material';
import { Close, CardMembership, EmojiEvents } from '@mui/icons-material';
import api from '../services/api';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';

type Tier = 'none' | 'bronze' | 'silver' | 'gold';

interface Summary {
  total_members: number;
  tier_none: number;
  tier_bronze: number;
  tier_silver: number;
  tier_gold: number;
  coupons_issued: number;
  coupons_redeemed: number;
  signups_today: number;
  signups_week: number;
  signups_month: number;
}

interface Member {
  user_id: number;
  membership_number: string;
  order_count: number;
  tier: Tier;
  bronze_coupon: string | null;
  silver_coupon: string | null;
  gold_coupon: string | null;
  created_at: string;
  updated_at: string;
  user_email: string | null;
  display_name: string | null;
  full_name: string | null;
}

interface CouponInfo {
  tier: Tier;
  code: string;
  amount: number | null;
  usage_count: number;
  redemptions: Array<{ order_id: number; date_created: string; discount_amount: number }>;
}

interface Order {
  id: number;
  status: string;
  total_amount: string;
  currency: string;
  date_created_gmt: string;
  payment_method_title: string | null;
}

const TIER_COLOURS: Record<Tier, string> = {
  none: '#9e9e9e',
  bronze: '#cd7f32',
  silver: '#a8a9ad',
  gold: '#d4af37',
};

const TIER_THRESHOLDS: Record<Tier, { min: number; next: Tier | null; nextAt: number | null; discount: number | null }> = {
  none:   { min: 0,  next: 'bronze', nextAt: 5,  discount: null },
  bronze: { min: 5,  next: 'silver', nextAt: 10, discount: 10 },
  silver: { min: 10, next: 'gold',   nextAt: 20, discount: 15 },
  gold:   { min: 20, next: null,     nextAt: null, discount: 20 },
};

const TierChip: React.FC<{ tier: Tier }> = ({ tier }) => (
  <Chip
    size="small"
    label={tier.toUpperCase()}
    sx={{
      backgroundColor: TIER_COLOURS[tier],
      color: '#fff',
      fontWeight: 600,
      letterSpacing: 0.5,
    }}
  />
);

const Loyalty: React.FC = () => {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState<'all' | Tier>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [detail, setDetail] = useState<{ member: Member; coupons: CouponInfo[] } | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await api.get('/loyalty/summary');
      setSummary(res.data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/loyalty/members', {
        params: { search, tier: tierFilter },
      });
      setMembers(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [search, tierFilter]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);
  useEffect(() => {
    const t = setTimeout(fetchMembers, 250);
    return () => clearTimeout(t);
  }, [fetchMembers]);

  const openMember = async (userId: number) => {
    setSelectedUserId(userId);
    setDetailLoading(true);
    try {
      const [d, o] = await Promise.all([
        api.get(`/loyalty/members/${userId}`),
        api.get(`/loyalty/members/${userId}/orders`),
      ]);
      setDetail(d.data);
      setOrders(o.data);
    } catch (e) {
      console.error(e);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelectedUserId(null);
    setDetail(null);
    setOrders([]);
  };

  const paged = members.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Loyalty Scheme
      </Typography>

      {/* Summary cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card><CardContent>
            <Typography variant="body2" color="text.secondary">Total Members</Typography>
            <Typography variant="h4">{summary?.total_members ?? '—'}</Typography>
          </CardContent></Card>
        </Grid>
        {(['bronze', 'silver', 'gold'] as Tier[]).map(t => (
          <Grid item xs={12} sm={6} md={3} key={t}>
            <Card sx={{ borderTop: `4px solid ${TIER_COLOURS[t]}` }}><CardContent>
              <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>{t} tier</Typography>
              <Typography variant="h4">{summary ? (summary as any)[`tier_${t}`] : '—'}</Typography>
            </CardContent></Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card><CardContent>
            <Typography variant="body2" color="text.secondary">Sign-ups Today</Typography>
            <Typography variant="h5">{summary?.signups_today ?? '—'}</Typography>
            <Typography variant="caption" color="text.secondary">
              {format(new Date(), 'd MMM yyyy')}
            </Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card><CardContent>
            <Typography variant="body2" color="text.secondary">Sign-ups This Week</Typography>
            <Typography variant="h5">{summary?.signups_week ?? '—'}</Typography>
            <Typography variant="caption" color="text.secondary">
              {format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'd MMM')}
              {' – '}
              {format(endOfWeek(new Date(), { weekStartsOn: 1 }), 'd MMM')}
            </Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card><CardContent>
            <Typography variant="body2" color="text.secondary">Sign-ups This Month</Typography>
            <Typography variant="h5">{summary?.signups_month ?? '—'}</Typography>
            <Typography variant="caption" color="text.secondary">
              {format(startOfMonth(new Date()), 'd MMM')}
              {' – '}
              {format(endOfMonth(new Date()), 'd MMM yyyy')}
            </Typography>
          </CardContent></Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Search name, email, membership no."
          size="small"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(0); }}
          sx={{ flex: 1, minWidth: 260 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Tier</InputLabel>
          <Select
            value={tierFilter}
            label="Tier"
            onChange={e => { setTierFilter(e.target.value as any); setPage(0); }}
          >
            <MenuItem value="all">All tiers</MenuItem>
            <MenuItem value="gold">Gold</MenuItem>
            <MenuItem value="silver">Silver</MenuItem>
            <MenuItem value="bronze">Bronze</MenuItem>
            <MenuItem value="none">None</MenuItem>
          </Select>
        </FormControl>
      </Paper>

      {/* Members table */}
      <Paper>
        {loading && <LinearProgress />}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Membership #</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Tier</TableCell>
                <TableCell align="right">Completed Orders</TableCell>
                <TableCell>Coupons Issued</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paged.map(m => {
                const issued = ['bronze','silver','gold'].filter(t => (m as any)[`${t}_coupon`]).length;
                return (
                  <TableRow
                    key={m.user_id}
                    hover
                    onClick={() => openMember(m.user_id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>{m.membership_number}</TableCell>
                    <TableCell>{m.full_name?.trim() || m.display_name || '—'}</TableCell>
                    <TableCell>{m.user_email}</TableCell>
                    <TableCell><TierChip tier={m.tier} /></TableCell>
                    <TableCell align="right">{m.order_count}</TableCell>
                    <TableCell>{issued}</TableCell>
                  </TableRow>
                );
              })}
              {!loading && paged.length === 0 && (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>No members match</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={members.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      {/* Member detail dialog */}
      <Dialog open={!!selectedUserId} onClose={closeDetail} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Member Details</span>
          <IconButton onClick={closeDetail}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading || !detail ? (
            <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
          ) : (
            <>
              {/* Member card */}
              {(() => {
                const m = detail.member;
                const t = TIER_THRESHOLDS[m.tier];
                const progress = t.nextAt ? Math.min(100, Math.round((m.order_count / t.nextAt) * 100)) : 100;
                const need = t.nextAt ? Math.max(0, t.nextAt - m.order_count) : 0;
                return (
                  <Paper variant="outlined" sx={{ p: 2.5, mb: 2, borderLeft: `6px solid ${TIER_COLOURS[m.tier]}` }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">BinsAndBags Member</Typography>
                        <Typography variant="h5" sx={{ color: TIER_COLOURS[m.tier], fontWeight: 700 }}>
                          {m.membership_number}
                        </Typography>
                        <Typography variant="body1">{m.full_name?.trim() || m.display_name}</Typography>
                        <Typography variant="body2" color="text.secondary">{m.user_email}</Typography>
                      </Box>
                      <TierChip tier={m.tier} />
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="body2"><strong>Completed orders:</strong> {m.order_count}</Typography>
                    {t.discount && <Typography variant="body2"><strong>Current reward:</strong> {t.discount}% discount</Typography>}
                    {t.nextAt && (
                      <Box sx={{ mt: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'text.secondary', mb: 0.5 }}>
                          <span style={{ textTransform: 'capitalize' }}>{m.tier}</span>
                          <span style={{ textTransform: 'capitalize' }}>{t.next}</span>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={progress}
                          sx={{ height: 8, borderRadius: 4, '& .MuiLinearProgress-bar': { backgroundColor: TIER_COLOURS[m.tier] } }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {need > 0 ? `${need} more collection${need !== 1 ? 's' : ''} until ${t.next}` : 'Reached the next tier!'}
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                );
              })()}

              {/* Coupons */}
              <Typography variant="h6" sx={{ mt: 1, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmojiEvents /> Coupons
              </Typography>
              {detail.coupons.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>No coupons issued yet.</Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Tier</TableCell>
                        <TableCell>Code</TableCell>
                        <TableCell align="right">Discount</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Redeemed on order</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detail.coupons.map(c => (
                        <TableRow key={c.code}>
                          <TableCell><TierChip tier={c.tier} /></TableCell>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{c.code}</TableCell>
                          <TableCell align="right">{c.amount ? `${c.amount}%` : '—'}</TableCell>
                          <TableCell>
                            {c.usage_count > 0
                              ? <Chip size="small" label="Used" color="success" />
                              : <Chip size="small" label="Unused" variant="outlined" />}
                          </TableCell>
                          <TableCell>
                            {c.redemptions.map(r => (
                              <div key={r.order_id}>
                                #{r.order_id} — {format(new Date(r.date_created), 'dd MMM yyyy')} — £{Number(r.discount_amount).toFixed(2)}
                              </div>
                            ))}
                            {c.redemptions.length === 0 && '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Orders */}
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Previous Orders</Typography>
              {orders.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No orders found.</Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Order #</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Payment</TableCell>
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {orders.map(o => (
                        <TableRow key={o.id}>
                          <TableCell>#{o.id}</TableCell>
                          <TableCell>{o.date_created_gmt ? format(new Date(o.date_created_gmt), 'dd MMM yyyy') : '—'}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={(o.status || '').replace(/^wc-/, '')}
                              color={o.status === 'wc-completed' ? 'success' : o.status === 'trash' ? 'default' : 'warning'}
                            />
                          </TableCell>
                          <TableCell>{o.payment_method_title || '—'}</TableCell>
                          <TableCell align="right">£{Number(o.total_amount || 0).toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Loyalty;
